import React from 'react';
import clsx from 'clsx';

import { 
  Tooltip,
  Typography,
}                           from '@material-ui/core';

import { 
  makeStyles,
}                           from '@material-ui/core/styles';

import {
  ResourceRef,
}                           from '../../resource';

import {
  fmtCurrency,
}                           from '../../utils/formatters';

import {
  fmtInventoryStatus, 
  fmtObjectRetention,
}                           from '../formatters';

import { 
  StorageBytes, 
  StorageBytesPerDay,
  StorageObjects,
  StorageObjectsPerDay,
  fmtBucketRef,
}                           from '.';

import { 
  numericIntervalColumnFilterProps 
} from "../../utils/Mui.js";

export const useStyles = makeStyles((theme) => ({
  rightTableHead: {
    '& > span': {
      justifyContent: 'right',
    }
  },
}));

export function sortInventoryStatus(order) {
  return ({ data: inv1 }, { data: inv2 }) => {
    let a = fmtInventoryStatus(inv1);
    let b = fmtInventoryStatus(inv2);
    return (a<b?-1:(a>b?1:0)) * (order === 'asc' ? 1 : -1);
  };
}

export function sortStorageAcls(order) {
  return ({ data: acl1 }, { data: acl2 }) => {
    let val1 = acl1.length;
    if(acl1.indexOf('NONE')>=0) {
      val1=0;
    } else if(acl1.indexOf('ALL')>=0) {
      val1=Infinity;
    }
    let val2 = acl2.length;
    if(acl2.indexOf('NONE')>=0) {
      val2=0;
    } else if(acl2.indexOf('ALL')>=0) {
      val2=Infinity;
    }
    return (val1 - val2) * (order === 'asc' ? 1 : -1);
  };
}

export function fmtStoragePrincipal(storagePrincipal,userContext) {
  if(['ALL','NONE'].indexOf(storagePrincipal) >= 0) {
    return("Shared w/"+storagePrincipal);
  } else if(userContext) {
    if(userContext.clients && storagePrincipal in userContext.clients) {
      if(userContext.clients[storagePrincipal].ClientAccountAlias) {
        return userContext.clients[storagePrincipal].ClientAccountAlias;
      }
    }
  }
  return storagePrincipal;
}

export function fmtClientResourceRef(resourceIdOrArrayOfIds) {
  if(Array.isArray(resourceIdOrArrayOfIds)) {
    if(!resourceIdOrArrayOfIds.includes("NONE") && !resourceIdOrArrayOfIds.includes("ALL")) {
      return(
        <Tooltip 
          title={<ResourceRef value={resourceIdOrArrayOfIds} type="Client" format="multiline" linked={false} sorted/>} enterDelay={1000} leaveDelay={200} arrow>
           <Typography align="inherit" variant="inherit">{resourceIdOrArrayOfIds.length}</Typography>
        </Tooltip>
      );
    } else {
      return(
         <Typography align="inherit" variant="inherit">{resourceIdOrArrayOfIds.includes("NONE")?"NONE":"ALL"}</Typography>
      );
    }
  }
  return(null);
}

export function fmtStorageBytes(sizeInBytes) {
  return(<StorageBytes size={sizeInBytes}/>);
}
export function fmtStorageBytesPerDay(perDayTrendInBytes) {
  return(<StorageBytesPerDay trend={perDayTrendInBytes}/>);
}
export function fmtStorageObjects(objectCount) {
  return(<StorageObjects count={objectCount}/>);
}
export function fmtStorageObjectsPerDay(perDayTrendObjectCount) {
  return(<StorageObjectsPerDay trend={perDayTrendObjectCount}/>);
}

export function createStorageResourceColumnDefs(classes,uctx) {
  const columnDefs = [
    { name: 'ResourceId', options: { display: true } },
    { name: 'ClientAccountId', options: { display: false } },
    { name: 'Description', options: { display: true } },
    { name: 'Bucket', 
      options: { 
        display: false,
        filter: false,
        customBodyRender: fmtBucketRef 
    }},
    { name: 'Metrics.CloudWatch.Latest.Data.Bytes.Value', label: 'Bytes', 
      options: { 
        sortDescFirst: true,
        customBodyRender: fmtStorageBytes,
        setCellHeaderProps: () => {
          return {
            className: clsx({
              [classes.rightTableHead]: true,
            })
          };
        },
        setCellProps: () => ({align: "right"}),
        ...numericIntervalColumnFilterProps({label: 'Bytes'})
    }},
    { name: 'Metrics.CloudWatch.Latest.Data.Bytes.Trend', label: 'Bytes/d', 
      options: { 
        sortDescFirst: true,
        customBodyRender: fmtStorageBytesPerDay,
        setCellHeaderProps: () => {
          return {
            className: clsx({
              [classes.rightTableHead]: true,
            })
          };
        },
        setCellProps: () => ({align: "right"}),
        ...numericIntervalColumnFilterProps({label: 'Bytes/d'})
    }},
    { name: 'Metrics.CloudWatch.Latest.Data.Objects.Value', label: 'Objects', 
      options: { 
        sortDescFirst: true,
        customBodyRender: fmtStorageObjects,
        setCellHeaderProps: () => {
          return {
            className: clsx({
              [classes.rightTableHead]: true,
            })
          };
        },
      setCellProps: () => ({align: "right"}),
      ...numericIntervalColumnFilterProps({label: 'Objects'})
    }},
    { name: 'Metrics.CloudWatch.Latest.Data.Objects.Trend', label: 'Objects/d', 
      options: { 
        sortDescFirst: true,
        customBodyRender: fmtStorageObjectsPerDay,
        setCellHeaderProps: () => {
          return {
            className: clsx({
              [classes.rightTableHead]: true,
            })
          };
        },
        setCellProps: () => ({align: "right"}),
        ...numericIntervalColumnFilterProps({label: 'Objects/d'})
    }},
    { name: 'AuthorizedReadOnlyAccountIds', label: 'Readers', 
      options: { 
        customBodyRender: fmtClientResourceRef,
        setCellHeaderProps: () => {
          return {
            className: clsx({
              [classes.rightTableHead]: true,
            })
          };
        },
        setCellProps: () => ({align: "right"}),
        customFilterListOptions: { render: v => `Readers: ${fmtStoragePrincipal(v, uctx )}` },
        filterOptions: {
          renderValue: v => fmtStoragePrincipal(v, uctx )
        },
        sortDescFirst: true,
        sortCompare: sortStorageAcls
    }},
    { name: 'AuthorizedReadWriteAccountIds', label: 'Writers' , 
      options: { 
        customBodyRender: fmtClientResourceRef,
        setCellHeaderProps: () => {
          return {
            className: clsx({
              [classes.rightTableHead]: true,
            })
          };
        },
        setCellProps: () => ({align: "right"}),
        customFilterListOptions: { render: v => `Writers: ${fmtStoragePrincipal(v, uctx )}` },
        filterOptions: {
          renderValue: v => fmtStoragePrincipal(v, uctx )
        },
        sortDescFirst: true,
        sortCompare: sortStorageAcls
    }},
    { name: 'BucketConfiguration', label: 'Inventory', 
      options: { 
        display: false, 
        customBodyRender: fmtInventoryStatus,
        sortCompare: sortInventoryStatus,
      }
    },
    { name: 'BucketConfiguration.ObjectRetentionDays', label: 'Retention', 
      options: { 
        display: false, 
        customBodyRender: fmtObjectRetention,
      }
    },
    { name: "Metrics.Inventory.Latest.Data.Cost.Estimated.Monthly.Total", label: "Cost", 
      options: {
        customBodyRender: fmtCurrency,
        sortDescFirst: true,
        setCellProps: () => ({align: "right"}),
      } 
    },
    
    { name: 'SecurityConfiguration.EncryptionMode', label: 'Encryption', options: { display: false } },
    { name: 'ResourceType', options: { display: false, filter: false } },
    { name: 'StackUpdated', options: { display: false } },
  ];
  return columnDefs;
}

